// =============================================================================
// FacilityBookingScheduleV2.tsx — Bookings V2 page (Fortius-admin only).
//
// Page-level entry for the redesigned bookings experience. Mirrors the V1
// FacilityBookingSchedule data flow (facility types + day → adaptBookingsToSchedules
// → grid) but renders the V2 grid/modal and adds the design's page chrome: a teal
// eyebrow + bold date title (teal day-of-month) above the grid, matching Member
// Profile V2. The whole screen is scoped under `.ft-bookings-v2` + the shared
// <ConfigProvider theme={fortiusProfileTheme}> so the teal palette stays local.
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Dialog,
  Button,
} from '@mui/material';
import { ConfigProvider, Empty } from 'antd';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useQuery } from 'react-query';
import { BookingScheduleTableV2 } from './BookingScheduleTableV2';
import { useBookingService } from '../../../services/bookings/BookingServiceProvider';
import { FacilityScheduleForDay } from '../../../model/bookings/FacilityScheduleForDay';
import { CompactWeekCalendar } from '../../common/CompactCalendar';
import { FacilityTypeContext } from '../../../model/facilities/FacilityTypeContext';
import { useMemberService } from '../../../services/members/MemberServiceProvider';
import { PlayingMember } from '../../../model/members/PlayingMember';
import { adaptBookingsToSchedules } from '../facility/adaptBookingsToSchedules';
import { fortiusBookingsTheme } from './bookingsV2Theme';
import './fortius-bookings.css';

// Monday-first week for the MUI pickers, without altering date *formatting*
// (mirrors the antd/dayjs side in src/config/dateConfig.ts — week-start only).
const mondayFirstLocale = { ...enUS, options: { ...enUS.options, weekStartsOn: 1 as const } };

export const FacilityBookingScheduleV2: React.FC = () => {
  const bookingService = useBookingService();
  const memberService = useMemberService();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedFacilityType, setSelectedFacilityType] = useState<FacilityTypeContext | null>(null);
  const [selectedFacilityTypeId, setSelectedFacilityTypeId] = useState<string>(() => {
    return localStorage.getItem('selectedFacilityTypeId') || '';
  });
  const [currentFacilityIndex, setCurrentFacilityIndex] = useState(0);
  const [scheduleData, setScheduleData] = useState<FacilityScheduleForDay[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Tracks the facility-types fetch so we can tell "still loading" apart from
  // "loaded, but none accessible" (empty state) and "the fetch failed" (error
  // state) — otherwise the grid spinner, which only clears once a facility type
  // is selected, would spin forever. [FORT-279]
  const [facilityTypesLoading, setFacilityTypesLoading] = useState<boolean>(true);
  const [facilityTypesError, setFacilityTypesError] = useState<boolean>(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [facilityTypes, setFacilityTypes] = useState<FacilityTypeContext[]>([]);

  const browserLocale = new Intl.Locale(navigator.language);
  const isUK = browserLocale.baseName.startsWith('en-GB');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCalendarOpen(false);
  };

  const fetchScheduleData = async () => {
    setIsLoading(true);
    try {
      if (!selectedFacilityType) {
        setIsLoading(false);
        return;
      }

      const nextDay = new Date(selectedDate);
      nextDay.setDate(selectedDate.getDate() + 1);
      const days = await bookingService.getBookings(selectedDate, nextDay, selectedFacilityType);
      setScheduleData(adaptBookingsToSchedules(days[0], selectedDate));
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setScheduleData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingSuccess = () => {
    fetchScheduleData();
  };

  const fetchMembers = async (): Promise<PlayingMember[]> => {
    return await memberService.getPlayingMembers();
  };

  const { data: members, isLoading: membersLoading, error: membersError } = useQuery(
    'members',
    fetchMembers
  );

  useEffect(() => {
    const fetchFacilityTypes = async () => {
      // Reset on every (re)fetch — e.g. a tenant switch re-memoises bookingService —
      // so the flags reflect the current fetch, not the previous client's result.
      setFacilityTypesLoading(true);
      setFacilityTypesError(false);
      try {
        const types = await bookingService.getFacilityTypes();
        setFacilityTypes(types);

        if (types.length > 0) {
          const savedTypeId = localStorage.getItem('selectedFacilityTypeId');
          const typeToSelect = savedTypeId
            ? types.find((type) => type.facilityType === savedTypeId) ?? types[0]
            : types[0];

          setSelectedFacilityTypeId(typeToSelect.facilityType);
          setSelectedFacilityType(typeToSelect);
        }
        // An empty list is a valid response (member with no accessible facility
        // types) — the empty-state gate below handles it once loading clears.
      } catch (error) {
        console.error('Error fetching facility types:', error);
        setFacilityTypes([]);
        setFacilityTypesError(true);
      } finally {
        setFacilityTypesLoading(false);
      }
    };
    fetchFacilityTypes();
  }, [bookingService]);

  useEffect(() => {
    if (selectedFacilityType) {
      fetchScheduleData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedFacilityType]);

  const handleFacilityTypeChange = (event: SelectChangeEvent) => {
    const typeId = event.target.value;
    setSelectedFacilityTypeId(typeId);

    const selectedType = facilityTypes.find((type) => type.facilityType === typeId);
    if (selectedType) {
      setSelectedFacilityType(selectedType);
      localStorage.setItem('selectedFacilityTypeId', typeId);
    }
  };

  if (membersLoading) return <div>Loading...</div>;
  if (membersError) return <div>Error: {(membersError as Error).message}</div>;

  // The facility-types fetch failed (the API client also raised an error toast).
  // Show an error state rather than the empty state so a server fault doesn't read
  // as "no facilities available", and rather than an indefinite spinner. [FORT-279]
  if (facilityTypesError) {
    return (
      <ConfigProvider theme={fortiusBookingsTheme}>
        <div
          className="ft-bookings-v2"
          data-testid="bookings-v2-error"
          style={{ background: 'var(--bk-app-bg)', borderRadius: 12, padding: isMobile ? 16 : 24 }}
        >
          <div className="bk-chrome">
            <div>
              <div className="bk-eyebrow">Bookings</div>
              <h1 className="bk-title">Couldn't load bookings</h1>
            </div>
          </div>
          <Empty description="We couldn't load the facilities. Please try again in a moment." style={{ marginTop: 48 }} />
        </div>
      </ConfigProvider>
    );
  }

  // Facility types loaded but none are accessible to this member (e.g. a
  // booking-only account without per-facility-type permissions). Show an empty
  // state instead of an indefinite spinner. [FORT-279]
  if (!facilityTypesLoading && facilityTypes.length === 0) {
    return (
      <ConfigProvider theme={fortiusBookingsTheme}>
        <div
          className="ft-bookings-v2"
          data-testid="bookings-v2-empty"
          style={{ background: 'var(--bk-app-bg)', borderRadius: 12, padding: isMobile ? 16 : 24 }}
        >
          <div className="bk-chrome">
            <div>
              <div className="bk-eyebrow">Bookings</div>
              <h1 className="bk-title">No bookable facilities</h1>
            </div>
          </div>
          <Empty
            description="No bookable facilities are available for your account. Please contact the club if you think this is a mistake."
            style={{ marginTop: 48 }}
          />
        </div>
      </ConfigProvider>
    );
  }

  const weekday = format(selectedDate, 'EEEE');
  const restOfDate = format(selectedDate, 'd MMMM yyyy');

  return (
    <ConfigProvider theme={fortiusBookingsTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={mondayFirstLocale}>
        <div
          className="ft-bookings-v2"
          data-testid="bookings-v2-screen"
          style={{ background: 'var(--bk-app-bg)', borderRadius: 12, padding: isMobile ? 16 : 24 }}
        >
          {/* Page chrome — teal eyebrow + bold date title */}
          <div className="bk-chrome">
            <div>
              <div className="bk-eyebrow">Bookings</div>
              <h1 className="bk-title">
                {weekday} <em>{restOfDate}</em>
              </h1>
            </div>
          </div>

          {/* Controls */}
          {isMobile ? (
            <>
              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Facility Type</InputLabel>
                  <Select
                    value={selectedFacilityTypeId}
                    onChange={handleFacilityTypeChange}
                    label="Facility Type"
                    disabled={isLoading}
                  >
                    {facilityTypes.map((type) => (
                      <MenuItem key={type.facilityType} value={type.facilityType}>
                        {type.facilityType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CompactWeekCalendar
                selectedDate={selectedDate}
                onChange={handleDateChange}
                onCalendarOpen={() => setCalendarOpen(true)}
              />

              <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} fullWidth maxWidth="xs">
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <DateCalendar
                    value={selectedDate}
                    onChange={(newDate) => {
                      if (newDate) {
                        handleDateChange(newDate);
                        setCalendarOpen(false);
                      }
                    }}
                    showDaysOutsideCurrentMonth={false}
                    disablePast
                    views={['day']}
                    sx={{
                      width: '100%',
                      '& .MuiPickersDay-root': { borderRadius: '8px' },
                      '& .MuiPickersDay-root.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      },
                    }}
                  />
                  <Button onClick={handleToday} variant="outlined" sx={{ mt: 1, mb: 1 }}>
                    Today
                  </Button>
                </Box>
              </Dialog>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newDate) => newDate && setSelectedDate(newDate)}
                disabled={isLoading}
                sx={{ width: 240 }}
                format={isUK ? 'dd/MM/yyyy' : 'MM/dd/yyyy'}
              />
              <FormControl sx={{ width: 200 }}>
                <InputLabel>Facility Type</InputLabel>
                <Select
                  value={selectedFacilityTypeId}
                  onChange={handleFacilityTypeChange}
                  label="Facility Type"
                  disabled={isLoading || facilityTypes.length === 0}
                >
                  {facilityTypes.map((type) => (
                    <MenuItem key={type.facilityType} value={type.facilityType}>
                      {type.facilityType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : scheduleData.length > 0 ? (
            members && (
              <BookingScheduleTableV2
                schedules={scheduleData}
                onBookingSuccess={handleBookingSuccess}
                selectedDate={selectedDate}
                initialFacilityIndex={currentFacilityIndex}
                onFacilityChange={setCurrentFacilityIndex}
                members={members}
              />
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 3, color: 'var(--bk-muted)' }}>No schedule data available</Box>
          )}
        </div>
      </LocalizationProvider>
    </ConfigProvider>
  );
};

export default FacilityBookingScheduleV2;
